/**
 * The way back, shown in the top bar. Every page says what it's called and
 * where "up" is; the bar then goes back to where you actually came from when
 * that was another page in the app (Today, a day, Search), and up otherwise.
 */

export type NavType = "PUSH" | "POP" | "REPLACE";

/** One page visit this session, by its router location key. */
export interface NavEntry {
  key: string;
  path: string;
}

/** Where "up" is from a page, used when there's no page to go back to. */
export interface PageParent {
  to: string;
  label: string;
}

export type BackTarget =
  | { kind: "history"; label: string; delta: number }
  | { kind: "parent"; label: string; to: string };

/** Keeps a copy of this tab's history in step with the router. */
export function nextStack(stack: NavEntry[], entry: NavEntry, type: NavType): NavEntry[] {
  if (type === "PUSH") return [...stack, entry];
  if (type === "REPLACE") return stack.length ? [...stack.slice(0, -1), entry] : [entry];
  // POP: back or forward to a visit we know, or a fresh load we don't.
  const at = stack.findIndex((e) => e.key === entry.key);
  if (at >= 0) return stack.slice(0, at + 1);
  return stack.length ? [...stack, entry] : [entry];
}

function isUnder(path: string, parent: string): boolean {
  return path.startsWith(parent.endsWith("/") ? parent : `${parent}/`);
}

/**
 * Where the bar's back goes, or null on a page with nowhere to go (a tab page).
 * The previous page wins when we know its name, unless it's the same page (a
 * view switch) or below this one (you came up from it with a link, so going
 * "back" would just loop).
 */
export function pickBack(
  stack: NavEntry[],
  titles: Record<string, string | undefined>,
  parent: PageParent | null,
): BackTarget | null {
  if (!parent) return null;
  const current = stack[stack.length - 1];
  if (current) {
    for (let i = stack.length - 2; i >= 0; i--) {
      const prev = stack[i];
      if (!prev) break;
      if (prev.path === current.path) continue;
      const title = titles[prev.key];
      if (title && !isUnder(prev.path, current.path)) {
        return { kind: "history", label: title, delta: i - (stack.length - 1) };
      }
      break;
    }
  }
  return { kind: "parent", label: parent.label, to: parent.to };
}
